import React, { useState, useRef, useEffect } from 'react'
import TemplatesModal from '../templates-modal'
import Dropzone from 'react-dropzone'

const Compose = ({selectedPlayer, selectedPlayers, adminPlayerId, channelId, onSendMessage, onSendFile, messages}) => {
  const [showTemplates, setShowTemplates] = useState(false)
  const [file, setFile] = useState(null)
  const [fileSrc, setFileSrc] = useState('')
  const [fileRatio, setFileRatio] = useState(1.0)
  const [isLoading, setIsLoading] = useState(false)
  const [dropzoneActive, setDropzoneActive] = useState(false)
  const [message, setMessage] = useState('')

  const fileInputRef = useRef()
  const composeRef = useRef()

  useEffect(() => {
    setIsLoading(false)
  }, [messages])

  const sendSingleMessage = e => onSendMessage({
    message: prefilledMessage(selectedPlayer),
    recipient: selectedPlayer.id,
    region: selectedPlayer.region,
    adminPlayerId: adminPlayerId,
    channelId: channelId,
    auto: false
  })

  const sendMultipleMessages = e => {
    selectedPlayers.forEach(recipient => onSendMessage({
      message: prefilledMessage(recipient),
      recipient: recipient.id,
      region: recipient.region,
      adminPlayerId: adminPlayerId,
      auto: true
    }))
  }

  const sendFile = e => {
    if (selectedPlayers) {
      selectedPlayers.forEach(recipient => onSendFile({
        file: file,
        fileRatio: fileRatio,
        region: recipient.region,
        recipient: recipient.id,
        adminPlayerId: adminPlayerId,
        auto: true
      }))
    } else {
      setIsLoading(true)
      onSendFile({
        file: file,
        fileRatio: fileRatio,
        region: selectedPlayer.region,
        channelId: channelId,
        adminPlayerId: adminPlayerId,
        auto: false
      })
    }
    setFile(null)
    setFileSrc('')
    setFileRatio(1.0)
    fileInputRef.current.value = null
  }

  const sendMessage = e => {
    if (!message.trim().length) return

    if (selectedPlayers) {
      sendMultipleMessages()
    } else {
      setIsLoading(true)
      sendSingleMessage()
    }

    setMessage('')
    composeRef.current.style.height = '0px'
  }

  const prefilledMessage = player => {
    const options = { 
      '[First Name]': player.firstName || '',
      '[Last Name]': player.lastName || '',
      '[Email]': player.email || '',
      '[Region]': player.regionName || ''
    }
    return Object.keys(options).reduce((acc, a) => acc.replaceAll(a, options[a]), message)
  }

  const cancelFile = () => {
    setFile(null)
    setFileSrc('')
    setFileRatio(1.0)
  }

  const sendButton = () => {
    const proc = file ? sendFile : sendMessage
    
    return <button className='send-button' onClick={proc}>
      Send
      <i className='fa fa-solid fa-paper-plane'></i>
    </button>
  }

  const onKey = e => {
    if (e.key == 'Enter' && e.ctrlKey) setMessage(message + '\r\n')
    if (e.key == 'Enter' && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault()
      if (file) {
        sendFile()
      } else {
        sendMessage()
      }
    }
  }

  const onChange = e => {
    setMessage(e.target.value)
    composeRef.current.style.height = `${Math.min(260, composeRef.current.scrollHeight)}px`
  }

  const fileLoaded = e => {
    const files = e.target.files

    if (files && files[0]) {
      const file = files[0]
      if (file.size > 10000000) return window.alert('File size limit is 10MB')
      if (!['image/jpeg', 'image/png'].includes(file.type)) return window.alert(' Only JPG and PNG files are allowed')

      readFile(file)
    }
  }

  const fileDropped = file => {
    if (file) {
      if (file.size > 10000000) return window.alert('File size limit is 10MB')
      readFile(file)
    }

    setDropzoneActive(false) 
  }

  const filePasted = file => {
    if (file) {
      if (file.size > 10000000) return window.alert('File size limit is 10MB')
      if (!['image/jpeg', 'image/png'].includes(file.type)) return window.alert(' Only JPG and PNG files are allowed')
      readFile(file)
    }
  }

  const readFile = f => {
    const fr = new FileReader()

    fr.onloadend = function() {
      setFileSrc(fr.result)
      setFile(f)
      setMessage('')

      var image = new Image()
      image.onload = function () {
        setFileRatio(this.width / this.height)
      }
      image.src = fr.result
    }
    fr.readAsDataURL(f)
  }

  return <div className='messages-cell chat-actions'>
    { isLoading && <div className='animated-spinner'></div> }
    { showTemplates && 
      <TemplatesModal onClose={() => setShowTemplates(false)} onSelect={(text) => setMessage(message + text)} /> 
    }
    { (selectedPlayer || selectedPlayers) &&
      <div
        className='message-wrapper'
        onDragEnter={() => setDropzoneActive(true)}
        onDragLeave={() => setDropzoneActive(false)}>
        <div className='message-input'>
          { 
            dropzoneActive && !file
            ? <Dropzone
                className='dropbox-container'
                accept='image/jpeg, image/png'
                onDrop={files => fileDropped(files[0])}>
                  <span>Drop the image here</span>
              </Dropzone>
            : <textarea
                id='new-message'
                className='new-message'
                placeholder='Type a message...'
                ref={composeRef}
                value={message}
                onChange={onChange}
                onPaste={e => filePasted(e.clipboardData.files[0])}
                onKeyPress={onKey}
                disabled={file ? true : false}
                role='textarea'
                rows='1' />
          }
        </div>
        <div className='message-buttons'>
          <div className='file-wrapper'>
            <label htmlFor='upload'>
              <input
                id='upload'
                ref={fileInputRef}
                type='file' 
                accept='image/png,image/jpeg' 
                className='form-control'
                style={{display: 'none'}}
                onChange={fileLoaded}
              />
              <img className='photo-preview' src={fileSrc || '/images/messages/image.svg'} />
            </label>
            { file && <div className='cancel-file' onClick={cancelFile}>Cancel</div> }
          </div>
          <img className='templates-button' src='/images/messages/template.svg' onClick={() => setShowTemplates(true)} />
          { sendButton() }
        </div>
      </div>
    }
  </div>
}

export default Compose
